import { GenericForm } from '@neo/blocks/forms/generic-form'
import { Button, ButtonWithLoader } from '@neo/primitives/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import type { ApiResponse } from '@shared/types'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { type Dispatch, type SetStateAction, useMemo } from 'react'
import { queryClient } from 'v2/configs/rq-setup'
import { useFlowSection } from 'v2/pages/flow/flow-section'
import { useAppNavigate } from 'v2/shared/hooks/use-app-navigate'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { settingsService } from 'v2/shared/services/settings-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { flowQueryKeys, getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import type { RDuplicateFlow } from 'v2/types/api/settings'
import { errorToast, successToast } from 'v2/utils/toasts'
import z from 'zod'

// The copy keeps the sub account link, so the only thing worth saying is when it
// could NOT be kept (the sub account is disabled or gone) — and only to orgs that
// can see subaccount UI at all. The tag handling itself is server-side; the flag
// gates nothing but this notice.
export const shouldWarnTagNotCopied = (
  data: Pick<RDuplicateFlow, 'source_was_tagged' | 'tag_copied'> | undefined,
  subaccountTaggingEnabled: boolean,
) => subaccountTaggingEnabled && !!data?.source_was_tagged && !data.tag_copied

interface DuplicateFlowDialogProps {
  state: string
  setState: Dispatch<SetStateAction<string>>
  /**
   * Optional flow name. When provided, used directly instead of looking up the
   * flow in the cached flows list query. Useful from contexts (e.g. the agent
   * builder page) where the flows list may not be in the query cache.
   */
  name?: string
}

export const DuplicateFlowDialog = ({ state, setState, name }: DuplicateFlowDialogProps) => {
  const navigate = useAppNavigate()
  const { noun, builderBase } = useFlowSection()
  const { subaccountTaggingEnabled } = useFeatureFlags()
  // Slim uuid point-lookup instead of fetching the whole flows list just to
  // resolve one name (SER-5833). Only fires when no name was passed in.
  const { data: flowLookup } = useQuery({
    queryKey: [...getSettingsQueryKeys().flowOptionsBase, 'lookup', state],
    queryFn: () => settingsService.listFlowOptions({ uuid: state }),
    enabled: !!state && !name,
  })
  const flow = name ?? flowLookup?.data.objects[0]?.name ?? 'Untitled Agent'
  const duplicateFlowMutation = useMutation({
    mutationFn: (name: string) => settingsService.duplicateFlow(state, name),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().flowsBase })
      setState('')
      successToast({
        title: 'Success',
        description: `The ${noun} has been duplicated successfully`,
      })
      if (shouldWarnTagNotCopied(response.data, subaccountTaggingEnabled)) {
        successToast({
          title: 'Sub account not copied',
          description:
            "This agent dropped the original agent's sub account link. Re-link it if needed.",
        })
      }
      // Open the freshly duplicated Agent's builder instead of staying put.
      const newPhloId = response.data?.phlo_id
      if (newPhloId) {
        navigate(`${builderBase}/${newPhloId}`)
      }
    },
    onError: (error) => {
      let errorText = getErrorText(error as AxiosError<ApiResponse<{ message: string }>>)
      errorText = Array.isArray(errorText) ? errorText.join(', ') : errorText
      errorToast({
        title: 'Error',
        description: errorText || `Failed to duplicate ${noun}`,
      })
    },
  })

  const handleDuplicate = (values: { name: string }) => {
    duplicateFlowMutation.mutate(values.name)
  }

  const defaultValues = useMemo(() => ({ name: `Copy of ${flow}` }), [flow])

  return (
    <Dialog open={!!state} onOpenChange={(o) => setState(o ? state : '')}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Duplicate {noun}</DialogTitle>
          <DialogDescription>
            Duplicate {flow} to create a new {noun}.
          </DialogDescription>
        </DialogHeader>
        <GenericForm
          fields={[
            {
              name: 'name',
              label: 'Name',
              componentType: 'input',
              zodValidation: z.string().min(1, 'Name is required'),
            },
          ]}
          defaultValues={defaultValues}
          onSubmit={handleDuplicate}
          includeSubmitButton={false}
          id="duplicate-flow-form"
        />
        <DialogFooter>
          <Button onClick={() => setState('')} variant="outline">
            Cancel
          </Button>
          <ButtonWithLoader
            loading={duplicateFlowMutation.isPending}
            disabled={duplicateFlowMutation.isPending}
            form="duplicate-flow-form"
            type="submit"
          >
            {duplicateFlowMutation.isPending ? 'Duplicating...' : 'Duplicate'}
          </ButtonWithLoader>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
